import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Modal } from 'antd';

import TagBadge from './TagBadge';
import { moveTargets, tagRefusal } from '../../utils/tags';

/**
 * Move to… (docs/plans/tags.md §5.1, §5.2): the drag's equal, and the way on a
 * phone. Lists every place the tag could go — the top level, then each tag of
 * the same owner, indented like the tree — and moves it to the END of the one
 * chosen. A place it cannot go stays listed, disabled, with the reason beside
 * it (under itself or beneath it, four levels deep, a restricted tag), so the
 * list explains itself.
 *
 * `onMove(parentId)` performs the move (null: the top level); a refusal it
 * throws — a sibling with the same name there — is shown here and the dialog
 * stays open.
 *
 * An ITEM tag's Move to… (Settings › Tags, b58) uses the same window with its
 * own places and words: `targets` (utils/itemTagSettings.js
 * itemTagPlaceTargets — the last level only, never a restricted tag), `name`
 * (its item's title), `note`, and `refusalOf(error)` for the item-tag route's
 * refusals.
 */
export default function TagMoveModal({
	open,
	onClose,
	onMove,
	tag,
	tags = [],
	targets: targetsProp = null,
	name = null,
	note = 'It goes last among the tags there, with the tags under it.',
	refusalOf = null,
}) {
	const [choice, setChoice] = useState(undefined);
	const [busy, setBusy] = useState(false);
	const [refusal, setRefusal] = useState(null);
	const targets = useMemo(() => {
		if (Array.isArray(targetsProp)) return targetsProp;
		return tag ? moveTargets(tags, tag) : [];
	}, [targetsProp, tags, tag]);
	const label = name || tag?.name || '';

	useEffect(() => {
		if (!open) return;
		setChoice(undefined);
		setBusy(false);
		setRefusal(null);
	}, [open]);

	const submit = async () => {
		if (choice === undefined) return;
		setBusy(true);
		setRefusal(null);
		try {
			await onMove(choice);
			onClose();
		} catch (error) {
			setRefusal(typeof refusalOf === 'function'
				? refusalOf(error)
				: tagRefusal(error, 'The tag could not be moved. Please try again.', { tags }));
		} finally {
			setBusy(false);
		}
	};

	return (
		<Modal
			open={open}
			title={tag ? `Move “${label}” to…` : 'Move to…'}
			okText="Move"
			onOk={submit}
			okButtonProps={{ loading: busy, disabled: choice === undefined }}
			onCancel={onClose}
			destroyOnHidden
			className="tag-move-modal"
		>
			{refusal ? <Alert type="error" showIcon className="tag-manager__refusal" message={refusal} /> : null}
			<p className="meta-line">{note}</p>
			<ul className="tag-move__list" role="listbox" aria-label="Where to move it">
				{targets.map(target => {
					const key = target.id || 'top';
					const selected = choice === target.id;
					return (
						<li key={key}>
							<button
								type="button"
								role="option"
								aria-selected={selected}
								aria-disabled={target.disabled || undefined}
								disabled={target.disabled}
								className={`tag-move__option${selected ? ' is-selected' : ''}`}
								style={{ '--tag-depth': target.depth }}
								title={target.reason || undefined}
								onClick={() => setChoice(target.id)}
								data-testid={`tag-move-${key}`}
							>
								{target.tag ? <TagBadge tag={target.tag} size="xs" /> : <span className="tag-move__top">Top level</span>}
								{target.reason ? <span className="tag-move__reason">{target.reason}</span> : null}
							</button>
						</li>
					);
				})}
			</ul>
		</Modal>
	);
}
